import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router';

import { findProject, PORTFOLIO, type PortfolioProject } from '@/entities/portfolio';

import { DocSection, DocToc } from './doc-parts';

/** 프로젝트가 자기 문구를 들고 있지 않으면 이걸 쓴다. */
const DEFAULT_INQUIRY = {
  label: '교육 문의하기',
  to: '/education/inquiry',
} as const;

/**
 * 우측 스티키 문의 위젯.
 *
 * 읽는 도중 어디서든 문의로 넘어갈 수 있어야 한다. 그래서 설명을 붙이지 않는다 —
 * 여기까지 읽은 사람에게 우리가 무슨 일을 하는지 다시 말할 이유가 없다.
 * 버튼 하나와 언제 답이 오는지만 둔다.
 */
function InquiryAside({
  inquiry,
  otherIds,
}: {
  inquiry: NonNullable<PortfolioProject['inquiry']>;
  otherIds: readonly { id: string; title: string }[];
}) {
  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-24">
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-5">
        <p className="text-[11px] font-medium text-muted-foreground">바로 문의하기</p>
        <Link
          to={inquiry.to}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 text-[14px] font-semibold text-primary-foreground hover:bg-primary/85"
        >
          {inquiry.label}
        </Link>
        <p className="text-center text-[12px] text-muted-foreground">
          영업일 기준 2일 안에 답변드립니다
        </p>
      </div>

      {otherIds.length > 0 ? (
        <nav aria-label="다른 프로젝트" className="flex flex-col gap-2">
          <p className="text-[11px] font-medium text-muted-foreground">다른 프로젝트</p>
          <ul className="flex flex-col gap-1">
            {otherIds.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/education/portfolio/${item.id}`}
                  className="block rounded-md px-2 py-1.5 text-[13px] leading-[1.6] text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {item.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </aside>
  );
}

/**
 * 포트폴리오 상세 — 마크다운 문서 형태.
 *
 * 좌측 목차 · 본문 · 우측 문의 CTA 3단이다. 읽는 사람은 위에서 아래로 훑다가
 * 어느 지점에서든 문의로 넘어갈 수 있어야 하는데, CTA 가 맨 아래에만 있으면
 * 끝까지 읽은 사람만 볼 수 있다.
 *
 * **카드의 반복이 되지 않게 쓴다.** 사례 카드가 결과 한 줄을 이미 말했으므로 본문은
 * 카드에 없던 것을 담는다 — 왜 그렇게 했는지, 무엇이 어려웠는지.
 */
export function PortfolioDetailPage(): ReactElement {
  const { projectId = '' } = useParams();
  const project = findProject(PORTFOLIO, projectId);

  if (!project) {
    return (
      <div className="u-container py-10">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-border border-dashed p-12 text-center">
          <p className="text-[15px] font-semibold">프로젝트를 찾을 수 없습니다</p>
          <Link to="/education" className="text-[13px] underline underline-offset-4">
            교육 소개로
          </Link>
        </div>
      </div>
    );
  }

  const others = PORTFOLIO.filter((item) => item.id !== project.id).map((item) => ({
    id: item.id,
    title: item.title,
  }));
  const inquiry = project.inquiry ?? DEFAULT_INQUIRY;

  return (
    <article className="u-container grid gap-10 py-12 lg:grid-cols-[11rem_minmax(0,1fr)_17rem] lg:items-start">
      <div className="lg:order-1">
        <DocToc sections={project.sections} />
      </div>

      <div className="flex min-w-0 flex-col gap-10 lg:order-2">
        <header className="flex flex-col gap-4">
          <nav aria-label="현재 위치" className="text-[12.5px] text-muted-foreground">
            <Link to="/education" className="underline-offset-4 hover:underline">
              교육
            </Link>
            <span aria-hidden="true"> / </span>
            <span>{project.title}</span>
          </nav>

          <span className="w-fit rounded-sm bg-ink-surface px-2.5 py-1 text-[11px] font-medium text-on-ink">
            {project.badge}
          </span>

          <h1 className="text-[clamp(1.5rem,1.2rem+1.2vw,2rem)] font-bold leading-[1.35] tracking-[-0.042em]">
            {project.headline}
          </h1>
        </header>

        <img
          src={project.thumb}
          alt={project.thumbAlt}
          className={
            project.thumbKind === 'logo'
              ? 'mx-auto h-32 w-auto object-contain'
              : 'w-full rounded-lg border border-border'
          }
        />

        {project.sections.map((section, index) => (
          <DocSection key={section.id} index={index + 1} section={section} />
        ))}

        {/* 좁은 화면에서는 사이드 CTA 가 맨 아래로 밀리므로 본문 끝에도 한 번 둔다 */}
        <div className="flex flex-col items-start gap-3 rounded-lg bg-secondary p-6 lg:hidden">
          <p className="text-[15px] font-semibold tracking-[-0.02em]">바로 문의하기</p>
          <Link
            to={inquiry.to}
            className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-[14px] font-semibold text-primary-foreground"
          >
            {inquiry.label}
          </Link>
        </div>
      </div>

      <div className="hidden lg:order-3 lg:block">
        <InquiryAside inquiry={inquiry} otherIds={others} />
      </div>
    </article>
  );
}
